import React from 'react';
import { Lead, Task, Tag } from '../../../types';
import { AppIcon } from '../../../components/icons/AppIcon';
import { useTaskStatus } from '../hooks/useTaskStatus';
import { TaskStatusIndicator } from './TaskStatusIndicator';
import { getInitials } from '../../../utils/formatters';

const countDaysInStage = (lead: Lead) => {
  const start = lead.stageEntryDate || lead.entryDate;
  if (!start) return 0;

  const entry = new Date(start);
  entry.setHours(0, 0, 0, 0);

  let end = new Date();
  if (lead.status === 'won' && lead.wonAt) end = new Date(lead.wonAt);
  else if (lead.status === 'lost' && lead.lostAt) end = new Date(lead.lostAt);
  end.setHours(0, 0, 0, 0);

  return Math.max(0, Math.ceil((end.getTime() - entry.getTime()) / 86400000));
};

const daysBadgeClass = (won: boolean, lost: boolean, days: number) => {
  if (won) return 'badge badge-sm badge-success';
  if (lost) return 'badge badge-sm badge-danger';
  if (days > 14) return 'badge badge-sm badge-warning';
  if (days > 7) return 'badge badge-sm badge-neutral';
  return 'badge badge-sm badge-success';
};

interface KanbanCardProps {
  lead: Lead;
  contactName?: string;
  tasks?: Task[];
  tags?: Tag[];
  stageAccentColor?: string;
  onClick: () => void;
  onChatClick?: (lead: Lead) => void;
}

const KanbanCard: React.FC<KanbanCardProps> = React.memo(({ lead, contactName, tasks = [], tags = [], stageAccentColor, onClick, onChatClick }) => {
  const taskStatus = useTaskStatus(tasks);
  const days = countDaysInStage(lead);
  const isOpen = lead.status === 'open';
  const isWon = lead.status === 'won';
  const isLost = lead.status === 'lost';
  const isAlert = isOpen && taskStatus.hasOverdueTasks;
  const displayName = contactName || lead.name;
  const isUnnamed = !displayName?.trim() || displayName.trim().toLowerCase() === 'sem nome';
  const ariaName = isUnnamed ? 'Lead sem nome' : displayName;
  const phone = lead.whatsApp?.trim();
  const productLine = lead.origin?.trim();
  const hasValue = lead.value > 0;
  const valueLabel = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(lead.value);
  const cardModifier = isWon
    ? 'deal-card--won'
    : isLost
      ? 'deal-card--lost'
      : isAlert
        ? 'deal-card--overdue'
        : '';
  const accent = isWon
    ? 'deal-card-accent--won'
    : isLost
      ? 'deal-card-accent--danger'
      : isAlert
        ? 'deal-card-accent--warning'
        : 'deal-card-accent--brand';
  const forceStatusAccent = isWon || isLost || isAlert;

  return (
    <li
      className={`deal-card group active:cursor-grabbing ${cardModifier}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Negociação ${ariaName}, valor ${hasValue ? valueLabel : 'não definido'}, ${days} dias na etapa`}
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.setData('type', 'card');
        e.dataTransfer.setData('leadId', lead.id);
        e.currentTarget.style.opacity = '0.5';
      }}
      onDragEnd={(e) => { e.currentTarget.style.opacity = '1'; }}
    >
      <div
        className={`deal-card-accent ${forceStatusAccent || !stageAccentColor ? accent : ''}`}
        style={!forceStatusAccent && stageAccentColor ? { backgroundColor: stageAccentColor } : undefined}
        aria-hidden="true"
      />

      <header className="deal-contact">
        <div className={`deal-avatar ${isUnnamed ? 'deal-avatar--unnamed' : ''}`} aria-hidden="true">
          {isUnnamed ? '?' : getInitials(displayName)}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className={`deal-name line-clamp-2 ${isUnnamed ? 'deal-name--empty' : ''}`}>
            {isUnnamed ? 'Lead sem nome' : displayName}
          </h3>
          {productLine ? <p className="deal-product line-clamp-1">{productLine}</p> : null}
          {isUnnamed && phone ? <span className="deal-phone">{phone}</span> : null}
        </div>
      </header>

      {hasValue ? (
        <p className={`deal-value ${isWon ? 'deal-value--won' : 'deal-value--brand'}`}>{valueLabel}</p>
      ) : (
        <p className="deal-value--empty">Sem valor definido</p>
      )}

      <footer className="deal-footer">
        <span className={daysBadgeClass(isWon, isLost, days)}>
          {isWon ? <AppIcon name="check" className="h-3 w-3 shrink-0" aria-hidden /> : <AppIcon name="clock" className="h-3 w-3 shrink-0" aria-hidden />}
          {isWon ? 'Fechado' : `${days}d`}
        </span>
        {isOpen ? <TaskStatusIndicator status={taskStatus.status} /> : null}
        {tags[0]?.name ? <span className="badge badge-sm badge-brand">{tags[0].name}</span> : null}
        {onChatClick ? (
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onChatClick(lead); }}
            className="deal-action"
            title="Abrir conversa"
            aria-label={`Abrir conversa com ${ariaName}`}
          >
            <AppIcon name="message" className="h-4 w-4" />
          </button>
        ) : null}
      </footer>
    </li>
  );
});

export default KanbanCard;
